import { Navigate, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Users, Package, Warehouse as WhIcon, ShoppingCart, Building2, LogOut, Sparkles, FileText, Truck, UserCog, Wrench, Bell, MessageSquare, BarChart3, Banknote, BookOpen, Shield, TrendingUp, CreditCard, Menu, X } from "lucide-react";
import Gr8AgenticPanel from "./Gr8AgenticPanel";
import Logo from "@/components/Logo";
import TrialBanner from "./TrialBanner";
import { useEffect, useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/sales", label: "Sales", icon: ShoppingCart, testid: "nav-sales" },
  { to: "/procurement", label: "Procurement", icon: Truck, testid: "nav-procurement" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/items", label: "Items & Stock", icon: Package, testid: "nav-items" },
  { to: "/warehouses", label: "Warehouses", icon: WhIcon, testid: "nav-warehouses" },
  { to: "/jobs", label: "Field Service", icon: Wrench, testid: "nav-jobs" },
  { to: "/operations", label: "Operations", icon: BookOpen, testid: "nav-operations" },
  { to: "/crm", label: "CRM Cases", icon: MessageSquare, testid: "nav-crm" },
  { to: "/hr", label: "HR & Payroll", icon: UserCog, testid: "nav-hr" },
  { to: "/finance", label: "Finance", icon: Banknote, testid: "nav-finance" },
  { to: "/financials", label: "Financial Statements", icon: TrendingUp, testid: "nav-financials" },
  { to: "/ledger", label: "AR Ledger", icon: FileText, testid: "nav-ledger" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/it", label: "IT & Compliance", icon: Shield, testid: "nav-it", roles: ["super_admin", "tenant_admin"] },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing", roles: ["super_admin", "tenant_admin"] },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  { to: "/admin/tenants", label: "Workspaces", icon: Building2, testid: "nav-tenants", roles: ["super_admin", "tenant_admin"] },
  { to: "/admin/quotes", label: "Quote management", icon: FileText, testid: "nav-quotes-admin", roles: ["super_admin", "tenant_admin"] },
];

export default function AppLayout() {
  const { user, tenant, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [agentOpen, setAgentOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close mobile nav whenever route changes (only if open)
  useEffect(() => { if (mobileNavOpen) setMobileNavOpen(false); }, [location.pathname]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for global "gr8agentic:ask" events (e.g. from MorningBrief) to open the panel
  useEffect(() => {
    const onAsk = (e) => {
      setAgentOpen(true);
      if (e.detail?.prompt) {
        setTimeout(() => {
          const inp = document.querySelector('[data-testid="gr8agentic-input"]');
          if (inp) {
            const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
            setter?.call(inp, e.detail.prompt);
            inp.dispatchEvent(new Event("input", { bubbles: true }));
          }
        }, 200);
      }
    };
    window.addEventListener("gr8agentic:ask", onAsk);
    return () => window.removeEventListener("gr8agentic:ask", onAsk);
  }, []);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-sm text-[#495057]">Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const visibleNav = NAV.filter((n) => !n.roles || n.roles.includes(user.role) || user.role === "super_admin");

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]" data-testid="app-layout">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#0A0A0A] text-white border-b border-white/10 flex items-center justify-between px-4 h-14" data-testid="mobile-topbar">
        <Link to="/dashboard" className="flex items-center gap-2"><Logo size={26} dark="#FFFFFF" accent="#FF4F00" /></Link>
        <button onClick={() => setMobileNavOpen(true)} data-testid="mobile-nav-open" aria-label="Open menu" className="p-2 -mr-2 hover:bg-white/10 rounded">
          <Menu size={22} />
        </button>
      </header>

      {/* Backdrop for mobile drawer */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-backdrop" />
      )}

      {/* Sidebar — drawer on mobile, fixed column on lg+ */}
      <aside
        data-testid="sidebar"
        className={`bg-[#0A0A0A] text-white flex flex-col border-r border-[#0A0A0A] fixed lg:static inset-y-0 left-0 z-50 w-72 lg:w-60 transform transition-transform duration-200 ease-out ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <Logo size={28} dark="#FFFFFF" accent="#FF4F00" />
          </Link>
          <button onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-close" aria-label="Close menu" className="lg:hidden p-1 hover:bg-white/10 rounded">
            <X size={20} />
          </button>
        </div>
        <div className="px-5 pt-2 pb-3 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/50 truncate">
          {tenant?.name || "—"}
        </div>
        <nav className="p-3 flex-1 overflow-y-auto">
          {visibleNav.map((n) => {
            const active = location.pathname === n.to || location.pathname.startsWith(n.to + "/");
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 mb-1 text-sm rounded-sm transition-colors duration-150 ${
                  active ? "text-white" : "text-white/80 hover:bg-white/5 hover:text-white"
                }`}
                style={active ? { background: "var(--tenant-accent, #FF4F00)" } : undefined}
              >
                <Icon size={16} />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={() => setAgentOpen(true)}
            data-testid="open-gr8agentic"
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm border text-white rounded-sm transition-colors hover:bg-white hover:text-[#0A0A0A]"
            style={{ borderColor: "var(--tenant-accent, #FF4F00)", color: "var(--tenant-accent, #FF4F00)" }}
          >
            <Sparkles size={14} /> Ask Gr8Agentic AI
          </button>
        </div>
        <div className="p-4 border-t border-white/10 text-xs">
          <div className="text-white/90 truncate" data-testid="current-user-name">{user.name}</div>
          <div className="text-white/40 truncate">{user.email}</div>
          <div className="text-[#FF4F00] uppercase tracking-[0.15em] mt-1 text-[10px]" style={{ color: "var(--tenant-accent, #FF4F00)" }}>{
            ({ super_admin: "Super Admin", tenant_admin: "Workspace Admin", sales_rep: "Sales Rep", accountant: "Accountant" }[user.role]) || user.role.replace("_", " ")
          }</div>
          <button
            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
            data-testid="logout-btn"
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:text-white hover:bg-white/5 rounded-sm"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-y-auto bg-white pt-14 lg:pt-0">
        <TrialBanner />
        <Outlet />
      </main>

      <Gr8AgenticPanel open={agentOpen} onClose={() => setAgentOpen(false)} />
    </div>
  );
}
